import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { AUTH_TYPES, FORMS, MODULES } from "../../const";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../LoadingSpinner";
import Dropdown from "../input/Dropdown";
import Button from "../input/Button";
import Grid from "../Grid";
import ButtonRenderer from "../renderer/ButtonRenderer";
import { isNumber } from "../../helper";
import SearchDropdown from "../input/SearchDropdown";
import {
  getFieldValuesByTableAndId,
  getOtherRefSearchDropdown,
  getTextByValueAndKey,
} from "../../api/keyvalue";
import { emptyValidator } from "../../validators";
import { consignmentTypeValidation } from "../validations/stakeholderCreation";
import { toast } from "react-toastify";
import { hasMultiple } from "../../common/logic";
import { ResizableScrollingTextRenderer } from "../renderer/StyleRenderer";
import DeleteButtonRenderer from "../renderer/DeleteButtonRenderer";

const FORM = FORMS.STAKEHOLDER.CONSIGNMENT.form;

const StakeholderConsignmentType = forwardRef(
  ({ dropdown, isLoading, currentValues = [], editMode }, ref) => {
    const dispatch = useDispatch();
    const [consignments, setConsignments] = useState([]);

    const [currentErrors, setCurrentErrors] = useState({});
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [deletedList, setDeletedList] = useState([]);
    const [conCount, setConCount] = useState(2);
    const [searchFieldReset, setSearchFieldReset] = useState(false);

    const [buttonClicked, setButtonClicked] = useState(false);
    const [mFields, setMFields] = useState({});
    const [currentConsignments, setCurrentConsignments] = useState({});

    const [consignmentTypeData, setConsignmentTypeData] = useState({
      sctConsignmentType: "",
      sctConsignmentTypeValue: "",
      sctStatus: 2,
      sctActive: 1,
      sctStatusValue: "",
      sctActiveValue: "",
      sctInvoicingCustomer: "",
      sctBillingCustomer: "",
    });

    const mandatoryFields = useSelector(
      (state) => state.stakeholder.consignmentTypeFormFields,
    );

    useEffect(() => {
      if (Object.entries(mandatoryFields).length <= 0) {
        setMFields({
          sctConsignmentType: {
            field: "sctConsignmentType",
            name: "Consignment Type",
            required: true,
          },
          sctStatus: {
            field: "sctStatus",
            name: "Status",
            required: true,
          },
          sctActive: {
            field: "sctActive",
            name: "Active",
            required: true,
          },
        });
      } else setMFields(mandatoryFields);
    }, [mandatoryFields]);

    useEffect(() => {
      (async () => {
        if (editMode) {
          let currentConsignments = [];
          let id = 2;
          for (let currentValue of currentValues) {
            const consignmentValue = await getTextByValueAndKey(
              "consignment_type",
              currentValue.sctConsignmentType,
            );
            const billingCustomerResponse = await getFieldValuesByTableAndId({
              tableName: "masterdata.tms_m_stakeholder",
              primaryKey: "stk_id",
              fieldNames: "stk_name",
              ids: currentValue.sctBillingCustomer,
            });
            const sctBillingCustomerName = billingCustomerResponse[0].field1;

            const invoicingCustomerResponse = await getFieldValuesByTableAndId({
              tableName: "masterdata.tms_m_stakeholder",
              primaryKey: "stk_id",
              fieldNames: "stk_name",
              ids: currentValue.sctInvoicingCustomer,
            });
            const sctInvoicingCustomerName =
              invoicingCustomerResponse[0].field1;

            currentConsignments.push({
              ...currentValue,
              sctStakeholder: currentValue.sctStakeholderId,
              sctBillingCustomerName,
              sctInvoicingCustomerName,
              consignmentValue,
              id,
            });
            ++id;
          }
          setConCount(id);
          setConsignments(currentConsignments);
        }
      })();
    }, [editMode, currentValues]);

    useEffect(() => {
      if (buttonClicked) {
        setConsignmentTypeData({
          sctConsignmentType: null,
          sctConsignmentTypeValue: "",
          sctInvoiceCustomer: null,
          sctBillingCustomer: null,
          sctStatus: 2,
          sctActive: 1,
          sctInvoicingCustomerName: "",
          sctBillingCustomerName: "",
        });

        setButtonClicked(false);
        setSearchFieldReset(true);
        setCurrentConsignments({});
      }
    }, [buttonClicked]);

    useEffect(() => {
      if (Object.keys(currentConsignments).length > 0) {
        setConsignmentTypeData({
          ...currentConsignments,
          sctConsignmentTypeValue: "",
          sctActiveValue: "",
        });
      }
    }, [editMode, currentConsignments]);

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (editMode) {
          const updateState = {
            modified: [],
            created: [],
            deleted: deletedList,
          };

          consignments.forEach((sct) => {
            const requestData = {
              sctId: sct.sctId || null,
              sctReference: sct.sctReference || "",
              sctCode: sct.sctCode || "",
              sctStakeholder: sct.sctStakeholder || null,
              sctConsignmentType: sct.sctConsignmentTypeId,
              sctDescription: sct.sctDescription || "",
              sctBillingCustomer: sct.sctBillingCustomerId,
              sctInvoicingCustomer: sct.sctInvoicingCustomerId,
              sctStatus: sct.sctStatus || null,
              sctActive: sct.sctActive || null,
              author: sct.author || "",
            };

            if (sct.sctId && sct.sctId !== null) {
              if (sct.modified) {
                updateState.modified.push(requestData);
              }
            } else {
              updateState.created.push(requestData);
            }
          });

          return updateState;
        } else {
          return consignments.map((sct) => ({
            sctId: sct.sctId || null,
            sctReference: sct.sctReference || "",
            sctCode: sct.sctCode || "",
            sctStakeholder: sct.sctStakeholder || null,
            sctConsignmentType: sct.sctConsignmentTypeId,
            sctDescription: sct.sctDescription || "",
            sctBillingCustomer: sct.sctBillingCustomerId,
            sctInvoicingCustomer: sct.sctInvoicingCustomerId,
            sctStatus: sct.sctStatus || null,
            sctActive: sct.sctActive || null,
            author: sct.author || "",
          }));
        }
      },
      resetForm: () => {
        setButtonClicked(true);
        setConsignments([]);
        setConsignmentTypeData({
          sctConsignmentType: null,
          sctConsignmentTypeValue: "",
          sctInvoiceCustomer: null,
          sctBillingCustomer: null,
          sctStatus: 2,
          sctActive: 1,
        });
        setSearchFieldReset(true);
      },
    }));

    const onAddClick = async () => {
      setIsAddLoading(true);

      let validationState = {};
      for (const [key, value] of Object.entries(mFields)) {
        validationState[key] = {
          validators: [emptyValidator()],
          name: value.name,
        };
      }

      const consignmentsErrors = consignmentTypeValidation(
        consignmentTypeData,
        validationState,
      );

      if (Object.keys(consignmentsErrors).length === 0) {
        const isDuplicate = hasMultiple({
          data: consignments,
          newItem: consignmentTypeData,
          validator: (item) =>
            item.sctConsignmentType === consignmentTypeData.sctConsignmentType,
          persistentIdentifier: "sctId",
        });

        if (isDuplicate) {
          toast.warn("This consignment type is already added!", {
            autoClose: 3000,
            containerId: "Main",
          });
          setIsAddLoading(false);
          return;
        }

        const sctStatusValue = await getTextByValueAndKey(
          "status",
          consignmentTypeData.sctStatus,
        );
        const sctActiveValue = await getTextByValueAndKey(
          "active",
          consignmentTypeData.sctActive,
        );
        const consignmentValue = await getTextByValueAndKey(
          "consignment_type",
          consignmentTypeData.sctConsignmentType,
        );
        const billingCustomerResponse = await getFieldValuesByTableAndId({
          tableName: "masterdata.tms_m_stakeholder",
          primaryKey: "stk_id",
          fieldNames: "stk_name",
          ids: consignmentTypeData.sctBillingCustomer,
        });
        const sctBillingCustomerName = billingCustomerResponse[0]?.field1 || "";

        const invoicingCustomerResponse = await getFieldValuesByTableAndId({
          tableName: "masterdata.tms_m_stakeholder",
          primaryKey: "stk_id",
          fieldNames: "stk_name",
          ids: consignmentTypeData.sctInvoicingCustomer,
        });
        const sctInvoicingCustomerName =
          invoicingCustomerResponse[0]?.field1 || "";

        if (!consignmentTypeData.sctId) {
          if (!consignmentTypeData.id) {
            setConsignments((prevState) => [
              ...prevState,
              {
                ...consignmentTypeData,
                id: conCount,
                consignmentValue,
                sctBillingCustomerName,
                sctBillingCustomerId: consignmentTypeData.sctBillingCustomer,
                sctInvoicingCustomerName,
                sctInvoicingCustomerId:
                  consignmentTypeData.sctInvoicingCustomer,
                sctConsignmentTypeId: consignmentTypeData.sctConsignmentType,
                sctActiveValue,
                sctStatusValue,
              },
            ]);

            setConCount((prevState) => prevState + 1);
          } else {
            setConsignments((prevState) =>
              prevState.filter((item) => item.id !== consignmentTypeData.id),
            );
            setConsignments((prevState) => [
              ...prevState,
              {
                ...consignmentTypeData,
                id: conCount,
                consignmentValue,
                sctBillingCustomerName,
                sctBillingCustomerId: consignmentTypeData.sctBillingCustomer,
                sctInvoicingCustomerName,
                sctInvoicingCustomerId:
                  consignmentTypeData.sctInvoicingCustomer,
                sctConsignmentTypeId: consignmentTypeData.sctConsignmentType,
                sctActiveValue,
                sctStatusValue,
              },
            ]);
          }
        } else {
          setConsignments((prevState) =>
            prevState.filter(
              (item) => item.sctId !== consignmentTypeData.sctId,
            ),
          );
          setConsignments((prevState) => [
            ...prevState,
            {
              ...consignmentTypeData,
              modified: true,
              id: consignmentTypeData.id || conCount,
              consignmentValue,
              sctBillingCustomerName,
              sctBillingCustomerId: consignmentTypeData.sctBillingCustomer,
              sctInvoicingCustomerName,
              sctInvoicingCustomerId: consignmentTypeData.sctInvoicingCustomer,
              sctConsignmentTypeId: consignmentTypeData.sctConsignmentType,
              sctActiveValue,
              sctStatusValue,
            },
          ]);
        }
        setButtonClicked(true);
      }
      setCurrentErrors(consignmentsErrors);
      setIsAddLoading(false);
    };

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <div className="ui dividing header">Consignment Type</div>
              <div className="two fields">
                <Dropdown
                  required={mFields.sctConsignmentType?.required}
                  label="Consignment Types"
                  error={currentErrors.sctConsignmentType}
                  form={FORM}
                  module={MODULES.Master}
                  field="sct_consignment_type"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { sctConsignmentType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Consignment Types"
                  options={dropdown.consignmentTypeOptions}
                  defaultValue=""
                  style={{ height: "20px" }}
                  value={consignmentTypeData.sctConsignmentType || ""}
                  onChange={(e, data) => {
                    setConsignmentTypeData((prevState) => ({
                      ...prevState,
                      sctConsignmentType: data.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <SearchDropdown
                  key={1}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="sct_billing_customer"
                  required={mFields.sctBillingCustomer?.required}
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setConsignmentTypeData((prevState) => ({
                      ...prevState,
                      sctBillingCustomer: null,
                    }));
                  }}
                  onResultSelect={(data) =>
                    setConsignmentTypeData((prevState) => ({
                      ...prevState,
                      sctBillingCustomer: data,
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "stakeholder_type",
                    value: "Billing Customer",
                  }}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_stakeholder",
                    primaryKey: "stk_id",
                    fieldName: "stk_name",
                    id: consignmentTypeData.sctBillingCustomer,
                  }}
                  label="Billing Customer"
                  multiGrid={true}
                  multiGridCurrentValue={
                    consignmentTypeData.sctBillingCustomerName
                  }
                  error={currentErrors.sctBillingCustomer}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { sctBillingCustomer: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />

                <SearchDropdown
                  key={1}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="sct_invoicing_customer"
                  reset={searchFieldReset}
                  required={mFields.sctInvoicingCustomer?.required}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setConsignmentTypeData((prevState) => ({
                      ...prevState,
                      sctInvoicingCustomer: null,
                    }));
                  }}
                  onResultSelect={(data) =>
                    setConsignmentTypeData((prevState) => ({
                      ...prevState,
                      sctInvoicingCustomer: data,
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "stakeholder_type",
                    value: "Invoicee Customer",
                  }}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_stakeholder",
                    primaryKey: "stk_id",
                    fieldName: "stk_name",
                    id: consignmentTypeData.sctInvoicingCustomer,
                  }}
                  label="Invoicing Customer"
                  multiGrid={true}
                  multiGridCurrentValue={
                    consignmentTypeData.sctInvoicingCustomerName
                  }
                  error={currentErrors.sctInvoicingCustomer}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { sctInvoicingCustomer: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
              </div>
              <div className="two fields">
                <Dropdown
                  required={mFields.sctStatus?.required}
                  label="Status"
                  error={currentErrors.sctStatus}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="sct_status"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { sctStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Status"
                  options={dropdown.statusOptions}
                  style={{ height: "20px" }}
                  value={consignmentTypeData.sctStatus}
                  onChange={(e, data) => {
                    setConsignmentTypeData((prevState) => ({
                      ...prevState,
                      sctStatus: data.value,
                    }));
                  }}
                />
                {/* <Dropdown
                  required={mFields.sctActive?.required}
                  label="Active"
                  error={currentErrors.sctActive}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="sct_active"
                  needDeletePermission={true}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { sctActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Active"
                  options={dropdown.activeOptions}
                  style={{ height: "20px" }}
                  value={consignmentTypeData.sctActive}
                  onChange={(e, data) => {
                    setConsignmentTypeData((prevState) => ({
                      ...prevState,
                      sctActive: data.value,
                    }));
                  }}
                /> */}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  template="reset"
                  onClick={() => {
                    setConsignmentTypeData({
                      sctConsignmentType: null,
                      sctConsignmentTypeValue: "",
                      sctInvoiceCustomer: null,
                      sctBillingCustomer: null,
                      sctStatus: 2,
                      sctActive: 1,
                    });
                    setSearchFieldReset(true); // Reset dropdowns
                  }}
                />

                <Button
                  template="addOrSave"
                  authenticated={true}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={`${FORM}`}
                  create={Object.keys(currentConsignments).length === 0}
                  onClick={onAddClick}
                  isLoading={isAddLoading}
                />
              </div>
              <Grid
                heading="Stakeholder Consignments"
                config={[
                  { headerName: "Type", field: "consignmentValue" },
                  {
                    headerName: "Billing Customer",
                    field: "sctBillingCustomerName",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Invoicing Customer",
                    field: "sctInvoicingCustomerName",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  { headerName: "Status", field: "sctStatusValue" },
                  { headerName: "Active", field: "sctActiveValue" },
                  {
                    field: "Edit",
                    filter: true,
                    cellRenderer: ButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.CREATE,
                      clickHandler: () => {
                        setCurrentConsignments(params.data);
                      },
                      label: "Edit",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode: editMode,
                    }),
                  },
                  {
                    field: "Delete",
                    filter: true,
                    cellRenderer: DeleteButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.DELETE,
                      clickHandler: () => {
                        if (params.data.sctId && params.data.sctId !== null) {
                          setDeletedList((prevState) => [
                            ...prevState,
                            params.data.sctId,
                          ]);
                        }
                        setConsignments((prevconsignments) =>
                          prevconsignments.filter(
                            (consignment) => consignment.id !== params.data.id,
                          ),
                        );
                      },
                      message:
                          "Are you sure you want to delete this Stakeholder Consignment Type?",
                      label: "Delete",
                      className: "ui red button",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode:
                        params.data.sctId !== null &&
                        isNumber(params.data.sctId),
                    }),
                  },
                ]}
                content={consignments}
              />
            </div>
          </form>
        )}
      </>
    );
  },
);

export default StakeholderConsignmentType;
